import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { useSeo } from '@/hooks/useSeo';
import { aboutStats, brandPrinciples, testimonials } from '@/mocks/chigua';

const CONTACT_ADDR = 'https://readdy.ai/api/form/daftbrbep053ijub1uu0';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function About() {
  useSeo({
    title: '关于我们 · 吃瓜路线',
    description:
      '认识吃瓜路线——一个帮你看懂微博、抖音、贴吧热门话题的围观指南站。了解我们的定位、发展理念、内容原则与联系方式。',
    keywords: '吃瓜路线,吃瓜路线关于我们,吃瓜路线围观指南,吃瓜路线联系方式',
  });

  const [status, setStatus] = useState<Status>('idle');
  const [msg, setMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = ((fd.get('company_alt') as string) || '').trim();
    if (honeypot) {
      setStatus('success');
      setMsg('感谢你的留言，我们会尽快回复你。');
      form.reset();
      return;
    }
    fd.delete('company_alt');
    setStatus('loading');
    try {
      const res = await fetch(CONTACT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const code = parsed?.code;
      const serverMsg = (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setMsg('感谢你的留言，我们会尽快回复你。');
        form.reset();
      } else {
        setStatus('error');
        setMsg(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMsg('网络开小差了，请稍后再试。');
    }
  };

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于吃瓜路线',
    description: '吃瓜路线的品牌介绍、定位、理念与联系方式。',
    mainEntity: { '@type': 'Organization', name: '吃瓜路线', slogan: '不迷路，只吃瓜' },
  };

  const contactLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系吃瓜路线',
    description: '通过表单或邮箱联系我们，提供话题线索与建议。',
  };

  return (
    <>
      <JsonLd data={aboutLd} />
      <JsonLd data={contactLd} />
      <Navbar />
      <main>
        {/* 页头 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="container-x py-10 md:py-14">
            <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl text-foreground-900 md:text-5xl">
              我们只做一件事，
              <br />
              <span className="text-primary-500">帮你把瓜吃得明明白白</span>
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-foreground-600">
              「吃瓜路线」是一个面向微博、抖音、贴吧等平台热门话题的围观指南站。我们相信，追热点不该是一场赶不上的信息洪流，
              而是一条可以慢下来、看清楚、再出发的清晰路线。
            </p>
          </div>
        </section>

        {/* 品牌故事 + 数据 */}
        <section className="container-x grid grid-cols-1 gap-10 py-12 md:py-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest text-accent-600">Our Story</p>
            <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">从「总是慢半拍」开始的品牌故事</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-8 text-foreground-700">
              <p>
                最初，「吃瓜路线」只是几位内容编辑之间的玩笑话。大家白天上班、晚上刷手机，常常发现第二天才后知后觉地知道大家昨晚在聊什么，
                只能尴尬地假装“啊，我也正要说这个”。
              </p>
              <p>
                后来我们想：能不能做一个站点，把微博、抖音、贴吧里讨论度最高的话题，连同它的来龙去脉、争议焦点一起整理出来，
                让任何人都能用几分钟追上当天的节奏？于是「吃瓜路线」诞生了。
              </p>
              <p>
                到今天，我们已经整理了几千条话题，把「围观」变成一件轻松、清晰又有边界感的事。我们不追着热度抢眼球，
                只想把你关心的每一颗瓜，都摆在你看得懂的位置上。
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 self-center">
            {aboutStats.map((s, i) => (
              <div
                key={s.label}
                className={`rounded-3xl border p-6 ${
                  i === 1
                    ? 'border-accent-200 bg-accent-100/70 text-accent-900'
                    : 'border-background-200 bg-background-50 text-foreground-800'
                }`}
              >
                <p className="font-heading text-3xl md:text-4xl">{s.value}</p>
                <p className="mt-2 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
            <div className="col-span-2 rounded-3xl bg-primary-500 p-6 text-background-50">
              <i className="ri-heart-3-fill text-xl" aria-hidden="true"></i>
              <p className="mt-3 font-heading text-lg leading-relaxed">只整理信息与讨论，不造谣、不传谣，对每一条瓜负责。</p>
            </div>
          </div>
        </section>

        {/* 定位与理念 */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="container-x">
            <div className="max-w-2xl">
              <p className="text-xs font-medium uppercase tracking-widest text-accent-600">Our Principles</p>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">我们的定位与内容原则</h2>
              <p className="mt-2 text-sm leading-6 text-foreground-500">四条不成文的规矩，支撑着每一次「吃瓜路线」的出品。</p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {brandPrinciples.map((p, i) => (
                <div key={p.title} className="rounded-3xl border border-background-200 bg-background-50 p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 font-heading text-primary-50">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 用户之声 */}
        <section className="container-x py-12 md:py-16">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-widest text-accent-600">Voices</p>
            <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">他们也在「吃瓜路线」上</h2>
          </div>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-3xl border border-background-200 bg-background-100 p-6">
                <p className="flex-1 text-sm leading-6 text-foreground-700">{t.text}</p>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 font-heading text-sm text-background-50">
                    {t.name.slice(0, 1)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.tag}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* 联系方式 */}
        <section id="contact" className="scroll-mt-20 bg-background-100 py-12 md:py-16">
          <div className="container-x grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-accent-600">Contact</p>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">聊聊你想吃的那颗瓜</h2>
              <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
                有想看透的话题、有不错的题材线索，或是单纯想给我们提点建议？都欢迎通过下面的方式找到我们。
              </p>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: 'ri-mail-line', title: '电子邮箱', desc: 'hello@chigualuxian.example' },
                  { icon: 'ri-time-line', title: '内容更新', desc: '每日整理推送，节假日也会营业' },
                  { icon: 'ri-focus-3-line', title: '我们在哪', desc: '聚合微博 · 抖音 · 贴吧全平台热点' },
                ].map((c) => (
                  <li key={c.title} className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-700">
                      <i className={`${c.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{c.title}</p>
                      <p className="mt-1 text-sm text-foreground-600">{c.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-900">给我们留言</h3>
              <form
                id="contact-form"
                data-readdy-form="daftbrbep053ijub1uu0"
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-1.5 block text-sm text-foreground-700">你的称呼</label>
                    <input
                      id="cf-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-1.5 block text-sm text-foreground-700">邮箱地址</label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-subject" className="mb-1.5 block text-sm text-foreground-700">留言主题</label>
                  <select
                    id="cf-subject"
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full rounded-xl border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:bg-background-50"
                  >
                    <option value="" disabled>选择留言主题</option>
                    <option value="话题线索">提供话题线索</option>
                    <option value="意见建议">网站建议</option>
                    <option value="合作交流">合作交流</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-message" className="mb-1.5 block text-sm text-foreground-700">留言内容</label>
                  <textarea
                    id="cf-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="想说的话，最多 500 字"
                    className="w-full resize-none rounded-xl border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-guard"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === 'loading' ? '正在提交…' : '提交留言'}
                </button>
                {status === 'success' && (
                  <p className="flex items-start gap-2 rounded-2xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true"></i>
                    {msg}
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-start gap-2 rounded-2xl bg-primary-100 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                    {msg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}